import { BreakpointObserver } from '@angular/cdk/layout';
import { EventEmitter, inject, Injectable, Output } from '@angular/core';

import { ReplaySubject, Subscription } from 'rxjs';

const Breakpoints = {
    XSmall: '(max-width: 575.98px)',
    Small: '(min-width: 576px) and (max-width: 991.98px)',
    Medium: '(min-width: 992px) and (max-width: 1199.98px)',
    Large: '(min-width: 1200px) and (max-width: 1399.98px)',
    XLarge: '(min-width: 1400px)',
};

export function getSizeQualifier(width: number) {
    if (width <= 420) return 'xs';
    if (width <= 992) return 'sm';
    if (width < 1200) return 'md';
    return 'lg';
}

export interface ScreenBreakpointFlags {
    isXSmall: boolean;
    isSmall: boolean;
    isMedium: boolean;
    isLarge: boolean;
    isXLarge: boolean;
}

/** Width (px) for modals with wide tables/forms; aligned with `ScreenService` / CDK breakpoints. */
export function wideDesignModalWidthPx(screen: ScreenBreakpointFlags): number {
    const vw = globalThis.window?.innerWidth ?? 1200;
    if (screen.isXSmall) {
        return Math.max(300, Math.min(560, vw - 24));
    }
    if (screen.isSmall) {
        return Math.min(720, vw - 32);
    }
    if (screen.isMedium) {
        return Math.min(1024, vw - 40);
    }
    if (screen.isLarge) {
        return Math.min(1200, vw - 48);
    }
    if (screen.isXLarge) {
        return 1292;
    }
    return 960;
}

@Injectable()
export class ScreenService {
    private readonly breakpointObserver$ = inject(BreakpointObserver);
    @Output() changed = new EventEmitter();
    @Output() xSmallScreenChanged = new ReplaySubject<boolean>();
    @Output() smallScreenChanged = new ReplaySubject<boolean>();
    @Output() mediumScreenChanged = new ReplaySubject<boolean>();
    @Output() screenChanged = new ReplaySubject<ScreenBreakpointFlags>();

    breakpointSubscription: Subscription;

    constructor() {
        this.breakpointSubscription = this.breakpointObserver$
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .subscribe(data => {
                this.xSmallScreenChanged.next(data.breakpoints[Breakpoints.XSmall]);
                this.smallScreenChanged.next(data.breakpoints[Breakpoints.Small] || data.breakpoints[Breakpoints.XSmall]);
                this.mediumScreenChanged.next(data.breakpoints[Breakpoints.Medium]);
                this.screenChanged.next({
                    isXSmall: data.breakpoints[Breakpoints.XSmall],
                    isSmall: data.breakpoints[Breakpoints.Small],
                    isMedium: data.breakpoints[Breakpoints.Medium],
                    isLarge: data.breakpoints[Breakpoints.Large],
                    isXLarge: data.breakpoints[Breakpoints.XLarge],
                });
                this.changed.next(data);
            });
    }

    private isLargeScreen() {
        const isLarge = this.breakpointObserver$.isMatched(Breakpoints.Large);
        const isXLarge = this.breakpointObserver$.isMatched(Breakpoints.XLarge);

        return isLarge || isXLarge;
    }

    public get sizes(): Record<string, boolean> {
        return {
            'screen-x-small': this.breakpointObserver$.isMatched(Breakpoints.XSmall),
            'screen-small': this.breakpointObserver$.isMatched(Breakpoints.Small),
            'screen-medium': this.breakpointObserver$.isMatched(Breakpoints.Medium),
            'screen-large': this.breakpointObserver$.isMatched(Breakpoints.Large),
            'screen-x-large': this.breakpointObserver$.isMatched(Breakpoints.XLarge),
        };
    }

    /** Same shape as `screenChanged` emissions, from current `BreakpointObserver` state. */
    getBreakpointFlags(): ScreenBreakpointFlags {
        const s = this.sizes;
        return {
            isXSmall: s['screen-x-small'],
            isSmall: s['screen-small'],
            isMedium: s['screen-medium'],
            isLarge: s['screen-large'],
            isXLarge: s['screen-x-large'],
        };
    }

    /** Optional `flags` for tests; defaults to live breakpoint state. */
    getWideDesignModalWidthPx(flags?: ScreenBreakpointFlags): number {
        return wideDesignModalWidthPx(flags ?? this.getBreakpointFlags());
    }
}
